"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

interface CookieConsentProps {
  className?: string;
}

export default function CookieConsent({ className = "" }: CookieConsentProps) {
  // Start with false to match server render, then check localStorage in useEffect
  const [showConsent, setShowConsent] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Check localStorage on mount to avoid hydration mismatch
  // Use queueMicrotask to defer state updates and avoid cascading renders
  useEffect(() => {
    queueMicrotask(() => {
      const consent = localStorage.getItem("cookie-consent");
      if (!consent) {
        setShowConsent(true);
      } else {
        const savedPreferences = JSON.parse(consent);
        // Ensure analytics is always enabled (it's essential for business operations)
        const updatedPreferences = {
          ...savedPreferences,
          necessary: true,
          analytics: true,
        };
        // Sync updated preferences back to localStorage
        localStorage.setItem(
          "cookie-consent",
          JSON.stringify(updatedPreferences)
        );
      }
    });
  }, []);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!showConsent || !banner) return;

    const root = document.documentElement;
    const updateOffset = () => {
      root.style.setProperty(
        "--cookie-consent-height",
        `${Math.ceil(banner.getBoundingClientRect().height)}px`
      );
    };
    updateOffset();

    if (typeof ResizeObserver === "undefined") {
      return () => root.style.removeProperty("--cookie-consent-height");
    }

    const observer = new ResizeObserver(updateOffset);
    observer.observe(banner);

    return () => {
      observer.disconnect();
      root.style.removeProperty("--cookie-consent-height");
    };
  }, [showConsent]);

  const acceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem("cookie-consent", JSON.stringify(allPreferences));
    setShowConsent(false);

    // Note: Google Analytics consent mode is handled in the GoogleAnalytics component
  };

  const acceptNecessary = () => {
    // Analytics is essential for tracking purchases and business metrics
    const necessaryOnly = {
      necessary: true,
      analytics: true, // Analytics is part of essential cookies
      marketing: false,
    };
    localStorage.setItem("cookie-consent", JSON.stringify(necessaryOnly));
    setShowConsent(false);

    // Note: Google Analytics consent mode is handled in the GoogleAnalytics component
  };

  if (!showConsent) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      data-cookie-consent
      className={`app-bottom-bar fixed bottom-0 left-0 right-0 z-50 backdrop-blur-sm border-t shadow-lg ${className}`}
    >
      <div className="mx-auto max-w-4xl px-2 sm:px-6 sm:py-4">
        <div className="flex min-h-11 items-center justify-between gap-2 sm:gap-4">
          {/* Cookie Info */}
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-info-muted sm:size-8">
              <span className="text-sm sm:text-lg">🍪</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-foreground sm:text-sm">
                <span className="sm:hidden">Cookies</span>
                <span className="hidden sm:inline">Cookie Notice</span>
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed hidden sm:block">
                We use cookies for payments, essential features, and purchase
                tracking
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={acceptNecessary}
              className="px-2.5 text-xs sm:w-auto sm:px-3 sm:text-sm"
            >
              Essential only
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={acceptAll}
              className="px-2.5 text-xs sm:w-auto sm:px-3 sm:text-sm"
            >
              Allow All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
